import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CreditCard, Landmark, Smartphone } from "lucide-react";
import TerminalSalePanel from "@/components/TerminalSalePanel";
import StayLengthPicker from "@/components/StayLengthPicker";
import TermsStep from "@/components/TermsStep";
import api, { formatApiErrorDetail } from "@/lib/api";
import LoyaltyBadge from "@/components/LoyaltyBadge";
import { toast } from "sonner";
import TicketConfirmation from "@/components/TicketConfirmation";
import { sizeLabel } from "@/lib/sizeLabels";

export default function CheckInModal({ open, onClose, spot, lotId, lot, settings, onDone }) {
  const sizeClasses = settings?.sizeClasses || ["Small", "Medium", "Pole", "Car Hauler", "Other"];
  const [form, setForm] = useState({
    name: "",
    phone: "",
    plate: "",
    email: "",
    truckSize: spot?.sizeClass || "Medium",
    tenancyType: "Daily",
    companyName: "",
    truckType: "",
    trailerType: "",
    mcNumber: "",
    dotNumber: "",
    licNumber: "",
    paymentMethod: "Card",
    cardLast4: "",
  });
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [sale, setSale] = useState(null);
  const [days, setDays] = useState("1");
  const [openEnded, setOpenEnded] = useState(false);
  const [terms, setTerms] = useState(null);
  const [termsKey, setTermsKey] = useState(0);
  const terminal = form.paymentMethod === "Terminal";
  const isOpen = form.tenancyType === "Daily" && openEnded;
  const daysMissing = form.tenancyType === "Daily" && !openEnded && !(Number(days) >= 1);
  const [loyalty, setLoyalty] = useState(null);
  const [profileMeta, setProfileMeta] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookup = async () => {
    if (!form.phone || form.phone.replace(/\D/g, "").length < 4) return;
    try {
      const { data } = await api.get(`/customers/lookup?phone=${encodeURIComponent(form.phone)}`);
      if (data) {
        setLoyalty(data.loyalty || null);
        setProfileMeta({ flag: data.flag || "", notes: data.notes || "" });
        setForm((f) => ({
          ...f,
          name: f.name || data.name || "",
          plate: f.plate || data.plate || "",
          companyName: f.companyName || data.companyName || "",
          truckType: f.truckType || data.truckType || "",
          trailerType: f.trailerType || data.trailerType || "",
          mcNumber: f.mcNumber || data.mcNumber || "",
          dotNumber: f.dotNumber || data.dotNumber || "",
          licNumber: f.licNumber || data.licNumber || "",
        }));
        toast.success(`Returning driver: ${data.name}`);
      } else {
        setLoyalty(null);
        setProfileMeta(null);
      }
    } catch (e) {
      if (e.response?.status === 409 && String(e.response?.data?.detail || "").includes("Terms")) setTermsKey((k) => k + 1);
      console.error("Customer lookup failed:", e?.message || e);
    }
  };

  const daily = settings?.dailyRate ?? 35;
  const monthly = settings?.monthlyRate ?? 450;
  const taxUnset = lot?.salesTaxPercent == null;
  const taxPct = taxUnset ? 0 : Number(lot.salesTaxPercent);
  const nDays = form.tenancyType === "Daily" && !openEnded ? Math.max(1, Number(days) || 1) : 1;
  const base = form.tenancyType === "Daily" ? daily * nDays : monthly;
  const tax = Math.round(base * taxPct) / 100;

  const submit = async () => {
    if (!form.name || !form.phone) {
      toast.error("Driver name and phone are required");
      return;
    }
    setLoading(true);
    if (terminal) {
      try {
        const { data } = await api.post("/terminal/checkin-sale", {
          lotId, spotId: spot.id, ...form, paymentMethod: "Card", cardLast4: null, days: nDays, openEnded: isOpen, terms,
        });
        setSale(data);
      } catch (e) {
      if (e.response?.status === 409 && String(e.response?.data?.detail || "").includes("Terms")) setTermsKey((k) => k + 1);
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start terminal sale");
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const payload = { lotId, spotId: spot.id, ...form, terms };
      if (form.tenancyType === "Daily") Object.assign(payload, { days: nDays, openEnded: isOpen });
      const { data } = await api.post("/sessions/checkin", payload);
      toast.success(`Checked in to spot ${spot.spotNumber}`);
      onDone();
      setConfirmed(data);
    } catch (e) {
      if (e.response?.status === 409 && String(e.response?.data?.detail || "").includes("Terms")) setTermsKey((k) => k + 1);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-slate-900 text-slate-100 sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-bold">
            Check In — Spot {spot?.spotNumber}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {terminal
              ? "Card present: the full stay total is charged now on the terminal."
              : form.tenancyType === "Daily"
                ? "Daily stays are charged in full at booking (days × rate + tax)."
                : "Pre-authorize the card on entry. Full amount is captured at check-out."}
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <TicketConfirmation session={confirmed} spot={spot} lot={lot} onClose={onClose} />
        ) : sale ? (
          <TerminalSalePanel
            sale={sale}
            onUpdate={setSale}
            onApproved={(session) => { onDone(); setConfirmed(session); }}
            onClose={() => { onDone(); onClose(); }}
          />
        ) : (<>
        {loyalty && <LoyaltyBadge loyalty={loyalty} />}

        {profileMeta && (profileMeta.flag || profileMeta.notes) && (
          <div
            data-testid="checkin-driver-flag"
            className={`rounded-lg border px-3 py-2 text-sm ${
              profileMeta.flag === "WatchList"
                ? "border-red-500/50 bg-red-950/40 text-red-200"
                : profileMeta.flag === "VIP"
                ? "border-yellow-500/50 bg-yellow-950/30 text-yellow-200"
                : "border-slate-700 bg-slate-800 text-slate-200"
            }`}
          >
            {profileMeta.flag && (
              <span className="font-bold">
                {profileMeta.flag === "WatchList" ? "WATCH-LIST" : "VIP"}
                {profileMeta.notes ? " · " : ""}
              </span>
            )}
            {profileMeta.notes}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Driver name *</Label>
            <Input
              data-testid="checkin-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Phone *</Label>
            <Input
              data-testid="checkin-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              onBlur={lookup}
              placeholder="954-555-0100"
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Plate</Label>
            <Input
              data-testid="checkin-plate"
              value={form.plate}
              onChange={(e) => set("plate", e.target.value)}
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Company name</Label>
            <Input
              data-testid="checkin-company"
              value={form.companyName}
              onChange={(e) => set("companyName", e.target.value)}
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Truck type</Label>
            <Select value={form.truckType} onValueChange={(v) => set("truckType", v)}>
              <SelectTrigger data-testid="checkin-trucktype" className="h-12 bg-slate-950 border-slate-700">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {["Semi / Tractor-Trailer", "Box Truck", "Car Hauler", "Bus", "Other"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Trailer type</Label>
            <Select value={form.trailerType} onValueChange={(v) => set("trailerType", v)}>
              <SelectTrigger data-testid="checkin-trailertype" className="h-12 bg-slate-950 border-slate-700">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {["Flatbed", "Reefer", "Dry Van", "Other / None"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label className="text-slate-300">Carrier IDs (optional)</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                data-testid="checkin-mc"
                value={form.mcNumber}
                onChange={(e) => set("mcNumber", e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))}
                placeholder="MC #"
                className="h-11 bg-slate-950 border-slate-700 font-data"
              />
              <Input
                data-testid="checkin-dot"
                value={form.dotNumber}
                onChange={(e) => set("dotNumber", e.target.value.replace(/\D/g, ""))}
                placeholder="DOT #"
                className="h-11 bg-slate-950 border-slate-700 font-data"
              />
              <Input
                data-testid="checkin-lic"
                value={form.licNumber}
                onChange={(e) => set("licNumber", e.target.value.toUpperCase())}
                placeholder="Lic #"
                className="h-11 bg-slate-950 border-slate-700 font-data"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-300">Truck size</Label>
            <Select value={form.truckSize} onValueChange={(v) => set("truckSize", v)}>
              <SelectTrigger data-testid="checkin-size" className="h-12 bg-slate-950 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {sizeClasses.map((s) => (
                  <SelectItem key={s} value={s}>{sizeLabel(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Tenancy</Label>
            <Select value={form.tenancyType} onValueChange={(v) => set("tenancyType", v)}>
              <SelectTrigger data-testid="checkin-tenancy" className="h-12 bg-slate-950 border-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                <SelectItem value="Daily">Daily</SelectItem>
                <SelectItem value="Monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.tenancyType === "Daily" && (
          <StayLengthPicker days={days} onChange={setDays} openEnded={openEnded} onOpenEnded={setOpenEnded} />
        )}

        {/* Payment method toggle */}
        <div className="mt-2 space-y-2">
          <Label className="text-slate-300">Payment method</Label>
          <div className={`grid gap-3 ${lot?.terminalEnabled ? "grid-cols-3" : "grid-cols-2"}`}>
            {lot?.terminalEnabled && (
              <button
                type="button"
                data-testid="checkin-method-terminal"
                onClick={() => set("paymentMethod", "Terminal")}
                className={`flex items-center gap-2 rounded-lg border-2 p-3 text-left ${
                  terminal ? "border-[#A0148C] bg-slate-800" : "border-slate-700 bg-slate-950"
                }`}
              >
                <Smartphone className="h-5 w-5 text-slate-300" />
                <div>
                  <p className="font-semibold">Card present</p>
                  <p className="text-xs text-slate-400">Terminal · paid now</p>
                </div>
              </button>
            )}
            <button
              type="button"
              data-testid="checkin-method-card"
              onClick={() => set("paymentMethod", "Card")}
              className={`flex items-center gap-2 rounded-lg border-2 p-3 text-left ${
                form.paymentMethod === "Card"
                  ? "border-[#A0148C] bg-slate-800"
                  : "border-slate-700 bg-slate-950"
              }`}
            >
              <CreditCard className="h-5 w-5 text-slate-300" />
              <div>
                <p className="font-semibold">Card</p>
                <p className="text-xs text-slate-400">Same price</p>
              </div>
            </button>
            <button
              type="button"
              data-testid="checkin-method-ach"
              onClick={() => set("paymentMethod", "ACH")}
              className={`flex items-center gap-2 rounded-lg border-2 p-3 text-left ${
                form.paymentMethod === "ACH"
                  ? "border-[#A0148C] bg-slate-800"
                  : "border-slate-700 bg-slate-950"
              }`}
            >
              <Landmark className="h-5 w-5 text-slate-300" />
              <div>
                <p className="font-semibold">ACH</p>
                <p className="text-xs text-slate-400">Same price</p>
              </div>
            </button>
          </div>
        </div>


        {form.paymentMethod === "Card" && (
          <div className="space-y-2">
            <Label className="text-slate-300">Card last 4 (mock — use 0000 to simulate decline)</Label>
            <Input
              data-testid="checkin-card-last4"
              maxLength={4}
              value={form.cardLast4}
              onChange={(e) => set("cardLast4", e.target.value.replace(/\D/g, ""))}
              placeholder="4242"
              className="h-12 bg-slate-950 border-slate-700"
            />
          </div>
        )}

        <div data-testid="checkin-price-preview" className="mt-2 rounded-lg border border-slate-800 bg-slate-950 p-4 font-data text-sm">
          <div className="flex justify-between text-slate-400">
            <span>Base ({form.tenancyType}{nDays > 1 ? ` × ${nDays} days` : ""})</span>
            <span>${base.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-400">
            <span data-testid="checkin-tax-label">Sales tax ({taxPct}%)</span>
            <span data-testid="checkin-tax-amount">${tax.toFixed(2)}</span>
          </div>
          {taxUnset && (
            <p data-testid="checkin-tax-unset-warning" className="mt-1 text-xs text-amber-400">
              Tax rate not set for this lot — owner can set it in Settings.
            </p>
          )}
          <div className="mt-2 flex justify-between border-t border-slate-800 pt-2 text-base font-bold text-slate-100">
            <span>{terminal ? "Charge now on terminal" : form.tenancyType === "Daily" ? "Charged now at booking" : "Capture at checkout"}</span>
            <span data-testid="checkin-total">{daysMissing ? "—" : `$${(base + tax).toFixed(2)}`}</span>
          </div>
        </div>

        <TermsStep lotId={lotId} driverName={form.name} onChange={setTerms} refreshKey={termsKey} />

        <Button
          data-testid="checkin-submit"
          onClick={submit}
          disabled={loading || daysMissing || !terms}
          className="h-14 w-full bg-[#A0148C] text-lg font-bold hover:bg-[#80106F]"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : daysMissing ? "Enter days to charge" : isOpen ? `Charge first day $${(base + tax).toFixed(2)} — auto every 25h` : terminal ? `Charge $${(base + tax).toFixed(2)} on Terminal` : form.tenancyType === "Daily" ? `Charge $${(base + tax).toFixed(2)} & Check In` : "Pre-Authorize & Check In"}
        </Button>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
